import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Search, Plus, Edit, Trash2, User, Phone, MoreVertical, Download, Clock, Star, AlertTriangle, UserX, UserCheck, Sparkles, Filter } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { exportToExcel } from "../lib/utils";
import TablePagination from "../components/TablePagination";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { ClienteFormDialog, mascararTelefone } from "../features/cliente-cadastro";
import { BotaoOfertas } from "@/features/tipos-cliente";

// URL da API
const API = '/api';

const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

// Função para calcular tempo desde cadastro
const getTimeSinceRegistration = (dateStr) => {
  if (!dateStr) return "Sem data";
  
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);
  
  if (diffDays === 0) return "Hoje";
  if (diffDays === 1) return "Ontem";
  if (diffDays < 7) return `${diffDays} dias`;
  if (diffWeeks === 1) return "1 semana";
  if (diffWeeks < 4) return `${diffWeeks} semanas`;
  if (diffMonths === 1) return "1 mês";
  if (diffMonths < 12) return `${diffMonths} meses`;
  if (diffYears === 1) return "1 ano";
  return `${diffYears} anos`;
};

// CPF e aniversário na lista: o banco guarda como veio de cada canal
// ("06316491662", "063.164.916-62", "2001-05-31", "31/05/2001").
const formatarCpfLista = (cpf) => {
  const d = String(cpf || "").replace(/\D/g, "");
  if (d.length !== 11) return cpf ? String(cpf) : "—";
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};
const formatarAniversario = (v) => {
  if (!v) return "—";
  const t = String(v).trim();
  const iso = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return t;
};

// Função para calcular a TAG do cliente
// A REGRA de segmento vive no BACKEND (api/v1/clientes/segments.py) e chega
// pronta em `cliente.segmento`. Aqui fica so' a apresentacao: rotulo, cor, icone
// e ordem de exibicao.
//
// Antes o calculo era feito aqui no navegador. Passou pro backend porque os
// Disparos em massa precisam montar publico e orcamento sobre a base inteira —
// com a regra nos dois lados, um limiar alterado num so' faria a tela dizer
// "312 VIPs" e a campanha enviar pra 289.
const SEGMENT_STYLES = {
  vip: {
    faixa: "border-l-amber-500",
    tag: "Cliente VIP",
    color: "bg-gradient-to-r from-amber-500 to-yellow-500 text-white",
    icon: Sparkles,
    priority: 1
  },
  recorrente: {
    faixa: "border-l-green-500",
    tag: "Recorrente",
    color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    icon: Star,
    priority: 2
  },
  em_risco: {
    // Laranja fixo: o tema troca `orange` pela cor da loja (sairia azul, igual ao "Novo")
    faixa: "border-l-[#f97316]",
    tag: "Em Risco",
    color: "bg-[#ffedd5] text-[#c2410c] dark:bg-[#7c2d12]/40 dark:text-[#fb923c]",
    icon: AlertTriangle,
    priority: 3
  },
  primeiro_pedido: {
    faixa: "border-l-purple-500",
    tag: "1º Pedido",
    color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
    icon: User,
    priority: 4
  },
  novo: {
    faixa: "border-l-blue-500",
    tag: "Novo",
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    icon: UserCheck,
    priority: 5
  },
  perdido: {
    faixa: "border-l-red-500",
    tag: "Perdido",
    color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    icon: UserX,
    priority: 6
  },
  ativo: {
    faixa: "border-l-slate-400",
    tag: "Ativo",
    color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
    icon: User,
    priority: 7
  }
};

// Fallback em "ativo" e' o mesmo default que a regra antiga usava quando nenhuma
// condicao casava — resposta sem `segmento` nao quebra a tela.
const getClientTag = (cliente) => SEGMENT_STYLES[cliente?.segmento] || SEGMENT_STYLES.ativo;

// R$ 1.234,56 — o quanto o cliente já gastou na loja
const reaisBR = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// Esqueleto enquanto a lista chega na primeira abertura (dono, 25/10/2026)
const CardsCarregando = () => (
  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-e2e="clientes-carregando">
    {Array.from({ length: 7 }, (_, i) => (
      <div key={i} className="bg-card rounded-xl border p-3 flex flex-col gap-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-6 w-12" />
        <Skeleton className="h-3 w-full" />
      </div>
    ))}
  </div>
);

const LinhasCarregando = () => (
  <div className="divide-y">
    {Array.from({ length: 8 }, (_, i) => (
      <div key={i} className="flex items-center gap-4 px-4 py-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Skeleton className="w-10 h-10 rounded-full shrink-0" />
          <div className="flex flex-col gap-1.5 flex-1">
            <Skeleton className="h-4 w-40 max-w-full" />
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-3 w-16 rounded-full" />
          </div>
        </div>
        <Skeleton className="h-4 w-24 hidden md:block" />
        <Skeleton className="h-4 w-20 hidden md:block" />
        <Skeleton className="h-8 w-28 hidden lg:block" />
        <Skeleton className="h-8 w-24 hidden sm:block" />
        <Skeleton className="h-8 w-8 shrink-0" />
      </div>
    ))}
  </div>
);

// Contagem de cada cartão do topo antes da 1ª resposta
const CONTAGENS_VAZIAS = { all: 0, vip: 0, recorrente: 0, novo: 0, primeiro_pedido: 0, em_risco: 0, perdido: 0, ativo: 0 };

export default function Clientes() {
  // Paginação no servidor (dono, 25/10/2026): só a página da tela vem do
  // backend, já filtrada; antes vinha a base inteira (3,8 MB) a cada abertura.
  const [clientes, setClientes] = useState([]);
  const [total, setTotal] = useState(0);
  const [tagCounts, setTagCounts] = useState(CONTAGENS_VAZIAS);
  const [searchTerm, setSearchTerm] = useState("");
  const [busca, setBusca] = useState("");
  const [tagFilter, setTagFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentCliente, setCurrentCliente] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clienteToDelete, setClienteToDelete] = useState(null);
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [loading, setLoading] = useState(false);
  const [primeiraCarga, setPrimeiraCarga] = useState(true);
  const [exportando, setExportando] = useState(false);
  // Abertura da tela e depois de salvar/excluir: o backend recalcula pedidos e
  // gasto antes de listar. Trocar de página, buscar e filtrar não recalcula.
  const recalcular = useRef(true);
  const [versao, setVersao] = useState(0);
  const pedido = useRef(0);

  // Busca espera a pessoa parar de digitar
  useEffect(() => {
    const t = setTimeout(() => setBusca(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reset página quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [busca, tagFilter]);

  useEffect(() => {
    fetchClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, busca, tagFilter, versao]);

  // Salvou ou excluiu: recarrega a página atual com os números recalculados
  const recarregar = () => {
    recalcular.current = true;
    setVersao((v) => v + 1);
  };

  const fetchClientes = async () => {
    const meu = ++pedido.current;
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Você precisa estar logado como administrador");
        return;
      }
      const params = { pagina: currentPage, por_pagina: itemsPerPage };
      if (busca) params.busca = busca;
      if (tagFilter !== "all") params.segmento = tagFilter;
      if (recalcular.current) params.recalcular = true;
      const { data } = await axios.get(`${API}/clientes/pagina`, { ...getAuthHeader(), params });
      if (meu !== pedido.current) return; // chegou depois de outra busca: descarta
      recalcular.current = false;
      // Excluiu o último cliente da última página: volta para a página anterior
      if (!(data.itens || []).length && data.total > 0 && currentPage > 1) {
        setCurrentPage(Math.ceil(data.total / itemsPerPage));
        return;
      }
      setClientes(data.itens || []);
      setTotal(data.total || 0);
      setTagCounts({ ...CONTAGENS_VAZIAS, ...(data.contagens || {}) });
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
      if (error.response?.status === 401) {
        toast.error("Sessão expirada. Faça login novamente.");
      } else {
        toast.error("Erro ao carregar clientes");
      }
    } finally {
      if (meu === pedido.current) {
        setLoading(false);
        setPrimeiraCarga(false);
      }
    }
  };

  // Exportar leva a base inteira: só aqui a lista completa é baixada
  const exportar = async () => {
    setExportando(true);
    try {
      const { data } = await axios.get(`${API}/clientes`, getAuthHeader());
      exportToExcel(data, "clientes", {
        nome: "Nome",
        telefone: "Telefone",
        email: "Email",
        cpf: "CPF",
        data_nascimento: "Data Nascimento",
        genero: "Gênero",
        endereco: "Endereço",
        created_at: "Data Cadastro"
      });
    } catch (error) {
      toast.error("Erro ao exportar os clientes");
    } finally {
      setExportando(false);
    }
  };

  // Função para carregar dados de teste
  const loadTestData = async () => {
    const testClientes = [
      {
        nome: "Maria Silva",
        telefone: "(11) 9.8765-4321",
        email: "maria.silva@email.com",
        cpf: "123.456.789-00",
        genero: "feminino",
        pedidos_count: 0,
        total_gasto: 0,
        last_order_date: null,
        orders_last_30_days: 0
      },
      {
        nome: "João Santos",
        telefone: "(11) 9.1234-5678",
        email: "joao.santos@email.com",
        cpf: "987.654.321-00",
        genero: "masculino",
        pedidos_count: 1,
        total_gasto: 45.90,
        last_order_date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        orders_last_30_days: 1
      },
      {
        nome: "Ana Costa",
        telefone: "(21) 9.5555-4444",
        email: "ana.costa@email.com",
        cpf: "111.222.333-44",
        genero: "feminino",
        pedidos_count: 4,
        total_gasto: 320.50,
        last_order_date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        orders_last_30_days: 4
      },
      {
        nome: "Pedro Oliveira",
        telefone: "(31) 9.7777-8888",
        email: "pedro.oliveira@email.com",
        cpf: "555.666.777-88",
        genero: "masculino",
        pedidos_count: 8,
        total_gasto: 890.00,
        last_order_date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        orders_last_30_days: 5
      },
      {
        nome: "Carla Mendes",
        telefone: "(41) 9.3333-2222",
        email: "carla.mendes@email.com",
        cpf: "999.888.777-66",
        genero: "feminino",
        pedidos_count: 3,
        total_gasto: 150.00,
        last_order_date: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
        orders_last_30_days: 0
      },
      {
        nome: "Roberto Lima",
        telefone: "(51) 9.1111-0000",
        email: "roberto.lima@email.com",
        cpf: "444.333.222-11",
        genero: "masculino",
        pedidos_count: 12,
        total_gasto: 1500.00,
        last_order_date: new Date(Date.now() - 70 * 24 * 60 * 60 * 1000).toISOString(),
        orders_last_30_days: 0
      }
    ];
    
    try {
      setLoading(true);
      for (const cliente of testClientes) {
        await axios.post(`${API}/clientes`, cliente, getAuthHeader());
      }
      toast.success("6 clientes de teste carregados!");
      recarregar();
    } catch (error) {
      console.error("Erro ao carregar dados de teste:", error);
      toast.error("Erro ao carregar dados de teste");
    } finally {
      setLoading(false);
    }
  };

  // Cadastro e edição moram em features/cliente-cadastro (refeito em 25/10/2026)
  const handleOpenNew = () => {
    setCurrentCliente(null);
    setDialogOpen(true);
  };

  const handleEdit = (cliente) => {
    setCurrentCliente(cliente);
    setDialogOpen(true);
  };

  // "Esse WhatsApp já é de Fulano → Abrir cadastro": a busca leve não traz o
  // endereço, então o cadastro completo vem da lista ou do servidor
  const abrirExistente = async (c) => {
    const daLista = clientes.find((x) => x.id === c.id);
    if (daLista) { setCurrentCliente(daLista); return; }
    try {
      const r = await axios.get(`${API}/clientes/${c.id}`, getAuthHeader());
      setCurrentCliente(r.data);
    } catch {
      setCurrentCliente(c);
    }
  };

  const [bloqueioExclusao, setBloqueioExclusao] = useState(null); // { cliente, pedidos }

  // Regra do dono (09/09/2026): quem já comprou não pode ser excluído. O
  // backend também barra (409); aqui a tela explica antes de pedir confirmação.
  const handleDelete = async (cliente) => {
    try {
      const { data } = await axios.get(`${API}/clientes/${cliente.id}/uso`, getAuthHeader());
      if (data && data.pedidos > 0) {
        setBloqueioExclusao({ cliente, pedidos: data.pedidos });
        return;
      }
    } catch (_) { /* sem resposta: deixa o backend decidir no DELETE */ }
    setClienteToDelete(cliente);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (clienteToDelete) {
      try {
        await axios.delete(`${API}/clientes/${clienteToDelete.id}`, getAuthHeader());
        toast.success("Cliente excluído!");
        recarregar();
      } catch (error) {
        console.error("Erro ao excluir cliente:", error);
        toast.error(error?.response?.data?.detail || "Erro ao excluir cliente");
      }
    }
    setDeleteDialogOpen(false);
    setClienteToDelete(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("pt-BR");
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Clientes</h1>
        <p className="text-muted-foreground mt-1">Gerencie seus clientes cadastrados</p>
      </div>

      {/* Barra de Pesquisa e Ações */}
      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[250px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Pesquisar por nome, telefone, email ou CPF..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        {/* Filtro por Tag */}
        <Select value={tagFilter} onValueChange={setTagFilter}>
          <SelectTrigger className="w-[200px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filtrar por status" />
          </SelectTrigger>
          <SelectContent>
            {/* Sem "(0)" antes da lista chegar: a contagem só aparece depois da 1ª carga */}
            <SelectItem value="all">{primeiraCarga ? "Todos" : `Todos (${tagCounts.all})`}</SelectItem>
            <SelectItem value="vip">
              <span className="flex items-center gap-2">
                <Sparkles className="w-3 h-3 text-amber-500" />
                Cliente VIP ({tagCounts.vip})
              </span>
            </SelectItem>
            <SelectItem value="recorrente">
              <span className="flex items-center gap-2">
                <Star className="w-3 h-3 text-green-600" />
                Recorrente ({tagCounts.recorrente})
              </span>
            </SelectItem>
            <SelectItem value="novo">
              <span className="flex items-center gap-2">
                <UserCheck className="w-3 h-3 text-blue-600" />
                Novo ({tagCounts.novo})
              </span>
            </SelectItem>
            <SelectItem value="primeiro_pedido">
              <span className="flex items-center gap-2">
                <User className="w-3 h-3 text-purple-600" />
                1º Pedido ({tagCounts.primeiro_pedido})
              </span>
            </SelectItem>
            <SelectItem value="em_risco">
              <span className="flex items-center gap-2">
                <AlertTriangle className="w-3 h-3 text-amber-600" />
                Em Risco ({tagCounts.em_risco})
              </span>
            </SelectItem>
            <SelectItem value="perdido">
              <span className="flex items-center gap-2">
                <UserX className="w-3 h-3 text-red-600" />
                Perdido ({tagCounts.perdido})
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
        
        <Button variant="outline" onClick={exportar} disabled={exportando}>
          <Download className="w-4 h-4 mr-2" />
          {exportando ? "Exportando..." : "Exportar"}
        </Button>
        {!primeiraCarga && tagCounts.all === 0 && (
          <Button variant="outline" onClick={loadTestData} className="border-dashed">
            Carregar Dados Teste
          </Button>
        )}
        <Button onClick={handleOpenNew}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Cliente
        </Button>
      </div>

      {/* Estatísticas por Tag. Cada card tem "Criar oferta" (27/10/2026): promoção, cupom,
          cartão fidelidade ou cashback só para aquele tipo de cliente. */}
      {primeiraCarga ? <CardsCarregando /> : (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <div
          className={`bg-card rounded-xl border p-3 cursor-pointer transition-all flex flex-col ${tagFilter === 'vip' ? 'ring-2 ring-amber-500' : 'hover:border-amber-300'}`}
          onClick={() => setTagFilter(tagFilter === 'vip' ? 'all' : 'vip')}
          title="Mais de 4 pedidos nos últimos 30 dias"
        >
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500 shrink-0" />
            <span className="text-xs text-muted-foreground">VIP</span>
          </div>
          <p className="text-xl font-bold mt-1">{tagCounts.vip}</p>
          <p className="text-[11px] text-muted-foreground leading-tight mt-1 font-medium">5+ pedidos em 30 dias</p>
          <div className="mt-auto pt-2"><BotaoOfertas tipo="vip" /></div>
        </div>
        <div
          className={`bg-card rounded-xl border p-3 cursor-pointer transition-all flex flex-col ${tagFilter === 'recorrente' ? 'ring-2 ring-green-500' : 'hover:border-green-300'}`}
          onClick={() => setTagFilter(tagFilter === 'recorrente' ? 'all' : 'recorrente')}
          title="3 ou 4 pedidos nos últimos 30 dias"
        >
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-green-600 shrink-0" />
            <span className="text-xs text-muted-foreground">Recorrente</span>
          </div>
          <p className="text-xl font-bold mt-1">{tagCounts.recorrente}</p>
          <p className="text-[11px] text-muted-foreground leading-tight mt-1 font-medium">3–4 pedidos em 30 dias</p>
          <div className="mt-auto pt-2"><BotaoOfertas tipo="recorrente" /></div>
        </div>
        <div
          className={`bg-card rounded-xl border p-3 cursor-pointer transition-all flex flex-col ${tagFilter === 'novo' ? 'ring-2 ring-blue-500' : 'hover:border-blue-300'}`}
          onClick={() => setTagFilter(tagFilter === 'novo' ? 'all' : 'novo')}
          title="Cliente cadastrado que ainda não fez pedido"
        >
          <div className="flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-blue-600 shrink-0" />
            <span className="text-xs text-muted-foreground">Novo</span>
          </div>
          <p className="text-xl font-bold mt-1">{tagCounts.novo}</p>
          <p className="text-[11px] text-muted-foreground leading-tight mt-1 font-medium">Cadastrado, ainda sem pedido</p>
          <div className="mt-auto pt-2"><BotaoOfertas tipo="novo" /></div>
        </div>
        <div
          className={`bg-card rounded-xl border p-3 cursor-pointer transition-all flex flex-col ${tagFilter === 'primeiro_pedido' ? 'ring-2 ring-purple-500' : 'hover:border-purple-300'}`}
          onClick={() => setTagFilter(tagFilter === 'primeiro_pedido' ? 'all' : 'primeiro_pedido')}
          title="Fez exatamente 1 pedido — boa hora para fidelizar"
        >
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-purple-600 shrink-0" />
            <span className="text-xs text-muted-foreground">1º Pedido</span>
          </div>
          <p className="text-xl font-bold mt-1">{tagCounts.primeiro_pedido}</p>
          <p className="text-[11px] text-muted-foreground leading-tight mt-1 font-medium">Apenas 1 pedido realizado</p>
          <div className="mt-auto pt-2"><BotaoOfertas tipo="primeiro_pedido" /></div>
        </div>
        <div
          className={`bg-card rounded-xl border p-3 cursor-pointer transition-all flex flex-col ${tagFilter === 'em_risco' ? 'ring-2 ring-amber-500' : 'hover:border-amber-300'}`}
          onClick={() => setTagFilter(tagFilter === 'em_risco' ? 'all' : 'em_risco')}
          title="Já comprou mas está há mais de 30 dias sem pedir"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="text-xs text-muted-foreground">Em Risco</span>
          </div>
          <p className="text-xl font-bold mt-1">{tagCounts.em_risco}</p>
          <p className="text-[11px] text-muted-foreground leading-tight mt-1 font-medium">30+ dias sem comprar</p>
          <div className="mt-auto pt-2"><BotaoOfertas tipo="em_risco" /></div>
        </div>
        <div
          className={`bg-card rounded-xl border p-3 cursor-pointer transition-all flex flex-col ${tagFilter === 'perdido' ? 'ring-2 ring-red-500' : 'hover:border-red-300'}`}
          onClick={() => setTagFilter(tagFilter === 'perdido' ? 'all' : 'perdido')}
          title="Mais de 60 dias sem comprar — provavelmente perdeu o hábito"
        >
          <div className="flex items-center gap-2">
            <UserX className="w-4 h-4 text-red-600 shrink-0" />
            <span className="text-xs text-muted-foreground">Perdido</span>
          </div>
          <p className="text-xl font-bold mt-1">{tagCounts.perdido}</p>
          <p className="text-[11px] text-muted-foreground leading-tight mt-1 font-medium">60+ dias sem comprar</p>
          <div className="mt-auto pt-2"><BotaoOfertas tipo="perdido" /></div>
        </div>
        <div
          className={`bg-card rounded-xl border p-3 cursor-pointer transition-all flex flex-col ${tagFilter === 'all' ? 'ring-2 ring-primary' : 'hover:border-primary/30'}`}
          onClick={() => setTagFilter('all')}
          title="Todos os clientes cadastrados na sua base"
        >
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="text-xs text-muted-foreground">Total</span>
          </div>
          <p className="text-xl font-bold mt-1">{tagCounts.all}</p>
          <p className="text-[11px] text-muted-foreground leading-tight mt-1 font-medium">Cadastrados na sua base</p>
        </div>
      </div>
      )}

      {/* Tabela de Clientes */}
      <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
        {primeiraCarga ? <LinhasCarregando /> : clientes.length === 0 && !loading ? (
          <div className="text-center py-12 text-muted-foreground">
            <User className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>Nenhum cliente encontrado</p>
            <Button variant="outline" className="mt-4" onClick={handleOpenNew}>
              <Plus className="w-4 h-4 mr-2" />
              Cadastrar Cliente
            </Button>
          </div>
        ) : (
          <>
          {/* Trocando de página: a lista atual fica esmaecida até a próxima chegar */}
          <Table className={loading ? "opacity-60 transition-opacity" : "transition-opacity"} data-e2e="clientes-tabela">
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>CPF</TableHead>
                <TableHead>Aniversário</TableHead>
                <TableHead>Última compra · Cadastro</TableHead>
                <TableHead>Pedidos · Gasto</TableHead>
                <TableHead className="w-16">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clientes.map(cliente => {
                const tag = getClientTag(cliente);
                const TagIcon = tag.icon;
                const timeSince = getTimeSinceRegistration(cliente.created_at);
                
                return (
                  <TableRow key={cliente.id} data-e2e="cliente-linha" data-segmento={cliente.segmento || "ativo"}>
                    {/* Tipo do cliente: faixa colorida na borda + selo com o nome (dono, 25/10/2026) */}
                    <TableCell className={`border-l-4 ${tag.faixa}`} title={tag.tag}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center shrink-0">
                          {cliente.foto ? (
                            <img src={cliente.foto} alt={cliente.nome} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                              <User className="w-5 h-5 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium leading-tight truncate">{cliente.nome}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5" data-e2e="cliente-telefone-linha">
                            <Phone className="w-3 h-3 shrink-0" />
                            {cliente.telefone ? mascararTelefone(cliente.telefone) : "sem telefone"}
                          </p>
                          <span className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${tag.color}`} data-e2e="status-selo">
                            <TagIcon className="w-3 h-3" />
                            {tag.tag}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm" data-e2e="col-cpf">{formatarCpfLista(cliente.cpf)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm" data-e2e="col-aniversario">{formatarAniversario(cliente.data_nascimento)}</span>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const lastIso = cliente.last_order_date;
                        if (!lastIso) return <span className="text-xs text-muted-foreground">sem compra</span>;
                        // Formata para America/Sao_Paulo e calcula dias passados
                        const dt = new Date(lastIso);
                        if (isNaN(dt.getTime())) return <span className="text-xs text-muted-foreground">—</span>;
                        const dataStr = dt.toLocaleDateString('pt-BR', {
                          timeZone: 'America/Sao_Paulo',
                          day: '2-digit', month: '2-digit', year: '2-digit',
                        });
                        // Diferença em dias inteiros, usando "hoje em São Paulo" como referência
                        const nowSP = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
                        const lastSP = new Date(dt.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
                        const diffMs = nowSP.setHours(0, 0, 0, 0) - lastSP.setHours(0, 0, 0, 0);
                        const diffDays = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
                        const label =
                          diffDays === 0 ? 'hoje' :
                          diffDays === 1 ? 'há 1 dia' :
                          diffDays <= 30 ? `há ${diffDays} dias` :
                          diffDays <= 60 ? 'há 1 mês' :
                          `há ${Math.floor(diffDays / 30)} meses`;
                        const colorClass =
                          diffDays <= 7 ? 'text-green-600 dark:text-green-400' :
                          diffDays <= 30 ? 'text-foreground' :
                          diffDays <= 60 ? 'text-[#f97316]' :
                          'text-red-500';
                        return (
                          <div className="flex flex-col leading-tight">
                            <span className="text-sm font-medium">{dataStr}</span>
                            <span className={`text-[10px] ${colorClass}`}>{label}</span>
                          </div>
                        );
                      })()}
                      <span className="text-[11px] text-muted-foreground flex items-center gap-1 mt-1" title="Tempo de cadastro">
                        <Clock className="w-3 h-3" />
                        cadastro {timeSince}
                      </span>
                    </TableCell>
                    <TableCell data-e2e="col-pedidos-gasto">
                      <div className="flex flex-col leading-tight">
                        <span className="text-sm font-semibold tabular-nums">
                          {cliente.pedidos_count || 0} {(cliente.pedidos_count || 0) === 1 ? "pedido" : "pedidos"}
                        </span>
                        <span className={`text-xs tabular-nums ${cliente.total_gasto > 0 ? "text-emerald-600 dark:text-emerald-400 font-medium" : "text-muted-foreground"}`}
                          title="Soma dos pedidos não cancelados">
                          {reaisBR(cliente.total_gasto)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(cliente)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(cliente)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          
          {/* Paginação */}
          {total > 0 && (
            <TablePagination
              currentPage={currentPage}
              totalItems={total}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(value) => {
                setItemsPerPage(value);
                setCurrentPage(1);
              }}
            />
          )}
        </>
        )}
      </div>

      <ClienteFormDialog open={dialogOpen} onOpenChange={setDialogOpen} cliente={currentCliente}
        onSalvo={recarregar} onAbrirExistente={abrirExistente} />

      {/* Dialog Confirmar Exclusão */}
      {/* Cliente com compras: não exclui, só explica */}
      <AlertDialog open={!!bloqueioExclusao} onOpenChange={(o) => { if (!o) setBloqueioExclusao(null); }}>
        <AlertDialogContent data-e2e="dialog-exclusao-bloqueada">
          <AlertDialogHeader>
            <AlertDialogTitle>Este cliente não pode ser excluído</AlertDialogTitle>
            <AlertDialogDescription>
              "{bloqueioExclusao?.cliente?.nome}" já tem {bloqueioExclusao?.pedidos} compra{bloqueioExclusao?.pedidos === 1 ? "" : "s"} registrada{bloqueioExclusao?.pedidos === 1 ? "" : "s"} no sistema.
              Excluir o cadastro deixaria esses pedidos sem cliente e quebraria o histórico e os relatórios.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Entendi</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-e2e="dialog-excluir-cliente">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o cliente "{clienteToDelete?.nome}"? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
